import React from 'react';
import styled from 'styled-components';
import { HealthCheckDefinition } from 'services/health';
import { HealthCheckState } from 'types/health';
import HealthCheckStatus from './HealthCheckStatus';

interface Props {
  checks: HealthCheckDefinition[];
  states: Record<string, HealthCheckState>;
  onRetry: (checkId: string) => void;
  rowLabel: string;
}

export interface MatrixColumn {
  ref: number;
  label: string;
}

export interface MatrixRow {
  id: number;
  label: string;
  cells: Map<number, HealthCheckDefinition>;
}

export const buildMatrix = (checks: HealthCheckDefinition[], defaultRowLabel: string) => {
  const getNumber = (meta: Record<string, unknown>, key: string): number | undefined => {
    const value = meta[key];
    if (typeof value === 'number') {
      return value;
    }
    if (typeof value === 'string') {
      const parsed = parseInt(value, 10);
      return Number.isNaN(parsed) ? undefined : parsed;
    }
    return undefined;
  };

  const getString = (meta: Record<string, unknown>, key: string): string | undefined => {
    const value = meta[key];
    return typeof value === 'string' ? value : undefined;
  };

  const columns = new Map<number, MatrixColumn>();
  const rows = new Map<number, MatrixRow>();
  let fallbackColumnId = 1000;
  let fallbackRowId = 2000;

  checks.forEach((check) => {
    const meta = check.meta ?? {};

    let columnId = getNumber(meta, 'columnId');
    let columnLabel = getString(meta, 'columnLabel');
    if (columnId === undefined) {
      columnId = getNumber(meta, 'dialectId');
      if (columnLabel === undefined) {
        columnLabel = getString(meta, 'dialectCode') ?? getString(meta, 'dialectName');
      }
    }
    if (columnId === undefined) {
      columnId = fallbackColumnId++;
    }
    columnLabel = columnLabel ?? check.label;
    if (!columns.has(columnId)) {
      columns.set(columnId, { ref: columnId, label: columnLabel });
    }

    let rowId = getNumber(meta, 'rowId');
    let rowLabelValue = getString(meta, 'rowLabel');
    if (rowId === undefined) {
      rowId = getNumber(meta, 'ref');
      if (rowLabelValue === undefined) {
        rowLabelValue = getString(meta, 'refName');
      }
    }
    if (rowId === undefined) {
      rowId = fallbackRowId++;
    }
    rowLabelValue = rowLabelValue ?? defaultRowLabel;

    let row = rows.get(rowId);
    if (!row) {
      row = { id: rowId, label: rowLabelValue, cells: new Map() };
      rows.set(rowId, row);
    }

    row.cells.set(columnId, check);
  });

  if (rows.size === 0) {
    const fallbackRow: MatrixRow = { id: 0, label: defaultRowLabel, cells: new Map() };
    checks.forEach((check, index) => {
      const columnId = fallbackColumnId + index;
      fallbackRow.cells.set(columnId, check);
      columns.set(columnId, { ref: columnId, label: check.label });
    });
    rows.set(0, fallbackRow);
  }

  const sortedColumns = Array.from(columns.values()).sort((a, b) =>
    a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }),
  );
  const sortedRows = Array.from(rows.values()).sort((a, b) => a.id - b.id);

  return { columns: sortedColumns, rows: sortedRows };
};

const HealthMatrixTable: React.FC<Props> = ({ checks, states, onRetry, rowLabel }) => {
  const { columns, rows } = React.useMemo(() => buildMatrix(checks, rowLabel), [checks, rowLabel]);

  if (columns.length === 0 || rows.length === 0) {
    return <EmptyMessage>No checks available.</EmptyMessage>;
  }

  return (
    <TableWrapper>
      <StyledTable>
        <thead>
          <tr>
            <RowHeaderCell as="th">{rowLabel}</RowHeaderCell>
            {columns.map((column) => (
              <HeaderCell key={column.ref}>{column.label}</HeaderCell>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <StyledRow key={row.id}>
              <RowHeaderCell>{row.label}</RowHeaderCell>
              {columns.map((column) => {
                const check = row.cells.get(column.ref);
                return (
                  <DataCell key={column.ref} $hasCheck={!!check}>
                    {check ? (
                      <HealthCheckStatus check={check} state={states[check.id]} onRetry={onRetry} mode="table" />
                    ) : (
                      <EmptyCell>—</EmptyCell>
                    )}
                  </DataCell>
                );
              })}
            </StyledRow>
          ))}
        </tbody>
      </StyledTable>
    </TableWrapper>
  );
};

const TableWrapper = styled.div`
  display: inline-block;
  width: max-content;
`;

const StyledTable = styled.table`
  width: auto;
  border-collapse: collapse;
`;

const HeaderCell = styled.th`
  text-align: center;
  min-width: 32px;
  max-width: 48px;
  padding: 4px 6px;
  font-size: 13px;
  font-weight: 600;
  color: #1f2937;
  background-color: #f3f4f6;
  border-bottom: 1px solid #e5e7eb;
  white-space: nowrap;
`;

const StyledRow = styled.tr`
  &:nth-child(even) {
    background-color: #fafafa;
  }
`;

const RowHeaderCell = styled.td`
  padding: 6px 8px;
  font-size: 13px;
  font-weight: 500;
  color: #111827;
  border-bottom: 1px solid #e5e7eb;
  white-space: nowrap;
  text-align: left;
`;

const DataCell = styled.td<{ $hasCheck: boolean }>`
  padding: 3px 4px;
  border-bottom: 1px solid #e5e7eb;
  min-width: 32px;
  max-width: 48px;
  text-align: center;
  vertical-align: middle;
  background-color: ${({ $hasCheck }) => ($hasCheck ? 'transparent' : '#f9fafb')};
`;

const EmptyCell = styled.span`
  color: #9ca3af;
  font-size: 12px;
  text-align: center;
  display: block;
`;

const EmptyMessage = styled.div`
  padding: 16px;
  font-size: 13px;
  color: #6b7280;
  border: 1px dashed #d1d5db;
  border-radius: 8px;
`;

export default HealthMatrixTable;
